import React, { useRef, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import OnboardingContainer from 'components/smart/onboarding-container/OnboardingContainer'
import rocket_2 from 'pages/onboarding-reset-password/assets/rocket_2.png'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import { useTheme } from '@mui/material/styles'
import AdxPhoneNumber from 'components/reusable/AdxPhoneNumber/AdxPhoneNumber'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { countries } from 'services/api/dropdown'
import { onboardingContactDetails } from 'services/api/authentication'
import { getPersonalData } from 'services/api/modules/staff-management/staffManagementDashboard'
import { useQuery, useMutation } from 'react-query'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import { authContext } from 'contexts/AuthContext'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = Yup.object({
  phone_country_id: Yup.string().nullable().required('Phone Code is required'),
  phone_no: Yup.string()
    .nullable()
    .required('Phone Number is required')
    .matches(/^[0-9\b]+$/, 'Phone Numbers can only contain numbers'),
  address: Yup.string().nullable().required('Home Address is required'),
  postcode: Yup.string()
    .nullable()
    .when('country', {
      is: 'Malaysia',
      then: Yup.string()
        .nullable()
        .matches(/^[0-9]{5}?$/, 'Postal Code must be in number, eg. 56000'),
      otherwise: Yup.string()
        .nullable()
        .matches(/^[a-z\d\s]+$/i, 'Postal Code must be alphanumeric, eg. S10 2GA'),
    })
    .required('Postal Code is required'),
  state: Yup.string().nullable().required('State is required'),
  country: Yup.string().nullable().required('Country is required'),
})

const OnboardingContactDetails = () => {
  const formikRef = useRef()
  const { auth, setAuthData } = useContext(authContext)

  const { componentHeight } = useTheme()
  const [initialValues, setInitialValues] = useState({
    phone_country_id: null,
    phone_no: null,
    address: null,
    postcode: null,
    state: null,
    country: 'Malaysia',
  })
  const navigate = useNavigate()
  const { data: countriesList } = useQuery('countriesList', async () => {
    const { data } = await countries()
    return data
  })
  const mutation = useMutation(onboardingContactDetails, {
    onSuccess: async (response) => {
      await setAuthData()
      navigate('/onboarding-lift-off')
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        console.log(error)
      } else {
        console.log(error)
      }
    },
  })
  const handleSubmitContactButton = (values, actions) => {
    mutation.mutate(values)
  }
  const handleGetContacts = async (staffID) => {
    try {
      let response = await getPersonalData(staffID)
      setInitialValues({
        phone_country_id: response?.data?.data?.phone_country_id
          ? response?.data?.data?.phone_country_id
          : 132, // Default Malaysia,
        phone_no: response?.data?.data?.phone_no ? response?.data?.data?.phone_no : '',
        address: response?.data?.data?.address ? response?.data?.data?.address : '',
        postcode: response?.data?.data?.postcode ? response?.data?.data?.postcode : '',
        state: response?.data?.data?.state ? response?.data?.data?.state : '',
        country: response?.data?.data?.country ? response?.data?.data?.country : 'Malaysia', // Default Malaysia,
      })
    } catch (error) {
      console.log(error)
    }
  }
  useEffect(() => {
    if (auth?.data?.profile_status?.contact === 1) {
      handleGetContacts(auth?.data?.staff?.id)
    }
  }, [auth])
  return (
    <OnboardingContainer
      srcRightPhoto={rocket_2}
      bgColorRight="alternate.main"
      topRightPhoto={'115%'}
      leftRightPhoto={'115%'}
      heightRightPhoto={'125%'}
      transformRightPhoto={`translate(${-100}%, ${-100}%)`}
    >
      <Box
        height={'100%'}
        display="flex"
        justifyContent={{ xs: 'center', lg: 'flex-end' }}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
      >
        <Box
          width={{ xs: '100%', lg: '500px' }}
          height={{ lg: '650px' }}
          mr={{ lg: 7.6875 }}
          p={{ xs: 2 }}
          position={'relative'}
        >
          <Box mb={4}>
            <Typography variant="body2" mb={1} color="alternate.main">
              Step 3
            </Typography>
            <Typography variant="body1" textAlign="justify">
              Contact Details
            </Typography>
          </Box>
          <Formik
            onSubmit={handleSubmitContactButton}
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            innerRef={formikRef}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleSubmit,
              isValid,
              dirty,
              setFieldValue,
              resetForm,
            }) => (
              <Form>
                <Box display="flex" gap={2} mb={3.75} justifyContent="space-between">
                  <AdxPhoneNumber
                    label="Phone Number *"
                    placeholder="E.g: 123456789"
                    sxInput={{ width: '100%' }}
                    dropdownValue={values.phone_country_id}
                    dropdownName="phone_country_id"
                    onDropdownChange={(value) => setFieldValue(`phone_country_id`, value)}
                    dropdownErrorMessage={touched.phone_country_id && errors.phone_country_id}
                    dropdownIsErrorShown={touched.phone_country_id && errors.phone_country_id}
                    inputValue={values.phone_no}
                    inputName="phone_no"
                    onInputChange={handleChange}
                    inputErrorMessage={touched.phone_no && errors.phone_no}
                    inputIsErrorShown={touched.phone_no && errors.phone_no}
                  />
                </Box>
                <Field
                  as={AdxInput}
                  label="Home Address *"
                  sxLabel={{
                    color: 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  size="standard"
                  placeholder="Home Address"
                  value={values.address}
                  name="address"
                  onChange={handleChange}
                  errorMessage={<ErrorMessage name="address" component={AdxErrorMessage} />}
                  isErrorShown={touched.address && errors.address}
                  sx={{ marginBottom: 2 }}
                  multiline
                  minRows={3}
                />
                <Box display="flex" gap={2} mb={2} justifyContent="space-between">
                  <Field
                    as={AdxInput}
                    label="Zip/Postcode *"
                    sxLabel={{
                      color: 'dark-text.main' /*, fontSize: "30px!important" */,
                    }}
                    size="standard"
                    placeholder="E.g : 43500"
                    value={values.postcode}
                    name="postcode"
                    onChange={handleChange}
                    errorMessage={<ErrorMessage name="postcode" component={AdxErrorMessage} />}
                    isErrorShown={touched.postcode && errors.postcode}
                    sx={{ width: '50%' }}
                  />
                  <Field
                    as={AdxDropdown}
                    label="State *"
                    disabled={values.country ? false : true}
                    sxLabel={{
                      color: 'dark-text.main' /*, fontSize: "30px!important" */,
                    }}
                    sx={{ width: '50%' }}
                    value={values.state}
                    name="state"
                    onChange={handleChange}
                    isErrorShown={touched.state && errors.state}
                    errorMessage={<ErrorMessage name="state" component={AdxErrorMessage} />}
                  >
                    {countriesList &&
                      values.country &&
                      countriesList.length !== 0 &&
                      countriesList
                        ?.find((node) => node.name === values.country)
                        ?.states.map((item) => (
                          <AdxMenuItem value={item.name} selectedvalue={values.state}>
                            {item.name}
                          </AdxMenuItem>
                        ))}
                  </Field>
                </Box>
                <Field
                  as={AdxDropdown}
                  label="Country *"
                  sxLabel={{
                    color: 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sx={{ width: '100%', marginBottom: 16 }}
                  value={values.country}
                  onChange={handleChange}
                  name="country"
                  isErrorShown={touched.country && errors.country}
                  errorMessage={<ErrorMessage name="country" component={AdxErrorMessage} />}
                >
                  {countriesList &&
                    countriesList.length !== 0 &&
                    countriesList
                      .filter((cc) => cc.country_code !== 'IL')
                      .map((item) => (
                        <AdxMenuItem value={item.name} selectedvalue={values.country}>
                          {item.name}
                        </AdxMenuItem>
                      ))}
                </Field>
                <Box
                  width={'100%'}
                  display="flex"
                  gap={2}
                  justifyContent="space-between"
                  position={{ lg: 'absolute' }}
                  bottom={{ lg: 0 }}
                  pr={{ lg: 4 }}
                >
                  <AdxButton
                    variant={'outlined'}
                    sx={{ width: componentHeight.standard }}
                    onClick={() => navigate('/onboarding-job-information')}
                  >
                    <ChevronLeftIcon />
                  </AdxButton>
                  <AdxButton
                    variant={'contained'}
                    key={1}
                    sx={{ width: '100%' }}
                    size={'standard'}
                    onClick={handleSubmit}
                  >
                    Next
                  </AdxButton>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Box>
    </OnboardingContainer>
  )
}

export default OnboardingContactDetails
